import React, { memo } from 'react';
import { FileText, Target, Workflow, ArrowRight } from 'lucide-react';
export function ReadmePage() {
  return <div className="max-w-4xl mx-auto py-12 px-8" data-id="element-1452">
      {/* Header */}
      <div className="mb-12" data-id="element-1453">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-1454">00_README</p>
        <h1 className="font-display font-bold text-5xl text-film-black mb-3 leading-none" data-id="element-1455">
          Film Notes
          <br data-id="element-1456" />
          Template Library
        </h1>
        <p className="text-xl text-film-gray max-w-2xl" data-id="element-1457">
          Social-first design system for an analog photography hub.
        </p>
        <div className="flex gap-3 mt-6" data-id="element-1458">
          <span className="font-mono text-[10px] uppercase tracking-wider bg-film-black text-white px-2 py-1" data-id="element-1459">
            v1.0
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider border border-film-border px-2 py-1" data-id="element-1460">
            May 2026
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider border border-film-border px-2 py-1" data-id="element-1461">
            Internal
          </span>
        </div>
      </div>

      {/* Three info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-1462">
        <div className="bg-white border border-film-border p-6 relative" data-id="element-1463">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-black" data-id="element-1464"></div>
          <FileText className="w-5 h-5 text-film-black mb-3" data-id="element-1465" />
          <h3 className="font-display font-bold text-film-black mb-2" data-id="element-1466">
            File Purpose
          </h3>
          <p className="text-sm text-film-gray leading-relaxed" data-id="element-1467">
            A reusable library of social media templates mapped to content
            categories and post formats. Built for fast production: swap photo,
            edit text, export.
          </p>
        </div>
        <div className="bg-white border border-film-border p-6 relative" data-id="element-1468">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-red" data-id="element-1469"></div>
          <Target className="w-5 h-5 text-film-red mb-3" data-id="element-1470" />
          <h3 className="font-display font-bold text-film-black mb-2" data-id="element-1471">
            Primary KPI
          </h3>
          <p className="text-sm text-film-gray leading-relaxed" data-id="element-1472">
            App installs + early usage (D1/D7 active). Subscription messaging is
            secondary. Keep TOFU lead with memory, ritual, coherence.
          </p>
        </div>
        <div className="bg-white border border-film-border p-6 relative" data-id="element-1473">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-orange" data-id="element-1474"></div>
          <Workflow className="w-5 h-5 text-film-orange mb-3" data-id="element-1475" />
          <h3 className="font-display font-bold text-film-black mb-2" data-id="element-1476">
            How to use
          </h3>
          <p className="text-sm text-film-gray leading-relaxed" data-id="element-1477">
            Pick a page → pick a template frame → duplicate → swap content.
            Naming follows the CAT_/FMT_ convention.
          </p>
        </div>
      </div>

      {/* Naming conventions */}
      <section className="mb-12" data-id="element-1478">
        <h2 className="font-display font-bold text-2xl text-film-black mb-1" data-id="element-1479">
          Naming Conventions
        </h2>
        <p className="text-sm text-film-gray mb-6" data-id="element-1480">
          Every template is named by category + format for fast retrieval.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1481">
          <div data-id="element-1482">
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-1483">
              Categories (CAT_)
            </p>
            <div className="border border-film-border bg-white" data-id="element-1484">
              {[['CAT_LAB', 'Lab Spotlight', 'Trust, local share-backs'], ['CAT_TIP', 'Pro Tip', 'Saves, authority'], ['CAT_FEATURE', 'App Feature', 'Product clarity'], ['CAT_COMMUNITY', 'Community', 'Proof, belonging'], ['CAT_GEAR', 'Gear & Film', 'Discovery, saves']].map(([code, name, goal]) => <div key={code} className="grid grid-cols-12 px-4 py-3 border-b border-film-border last:border-b-0 text-sm" data-id="element-1485">
                  <div className="col-span-4 font-mono text-xs text-film-red" data-id="element-1486">
                    {code}
                  </div>
                  <div className="col-span-4 font-display font-bold text-film-black" data-id="element-1487">
                    {name}
                  </div>
                  <div className="col-span-4 text-film-gray text-xs" data-id="element-1488">
                    {goal}
                  </div>
                </div>)}
            </div>
          </div>

          <div data-id="element-1489">
            <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-1490">
              Formats (FMT_)
            </p>
            <div className="border border-film-border bg-white" data-id="element-1491">
              {[['FMT_REEL_COVER', '1080×1920', '9:16'], ['FMT_CAROUSEL', '1080×1350', '4:5'], ['FMT_SINGLE', '1080×1080', '1:1'], ['FMT_STORY', '1080×1920', '9:16 + safe zones'], ['FMT_AD', 'Variant', 'Extra safe zones']].map(([code, size, ratio]) => <div key={code} className="grid grid-cols-12 px-4 py-3 border-b border-film-border last:border-b-0 text-sm" data-id="element-1492">
                  <div className="col-span-5 font-mono text-xs text-film-orange" data-id="element-1493">
                    {code}
                  </div>
                  <div className="col-span-4 font-mono text-xs text-film-black" data-id="element-1494">
                    {size}
                  </div>
                  <div className="col-span-3 text-film-gray text-xs" data-id="element-1495">
                    {ratio}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="mb-12" data-id="element-1496">
        <h2 className="font-display font-bold text-2xl text-film-black mb-6" data-id="element-1497">
          Pages in this file
        </h2>
        <div className="space-y-2" data-id="element-1498">
          {[['00_README', 'You are here. File purpose, naming, how to use.'], ['01_BRAND_DOSSIER', 'Positioning, personas, voice, color, type, logo, CTAs.'], ['02_TEMPLATES_FEED', 'Reel covers, carousels, singles for the IG feed.'], ['03_TEMPLATES_STORIES', 'Story frames with sticker-safe zones.'], ['04_TEMPLATES_PRINT_OFFLINE', 'A6 lab card, sticker sheet, QR rules.'], ['05_AD_VARIANTS', 'Paid variants of organic winners with extra safe zones.']].map(([code, desc]) => <div key={code} className="bg-white border border-film-border px-5 py-3 flex items-center justify-between hover:border-film-gray transition-colors group cursor-pointer" data-id="element-1499">
              <div className="flex items-center gap-4" data-id="element-1500">
                <span className="font-mono text-xs text-film-gray" data-id="element-1501">{code}</span>
                <span className="text-sm text-film-black" data-id="element-1502">{desc}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-film-gray group-hover:text-film-red transition-colors" data-id="element-1503" />
            </div>)}
        </div>
      </section>

      {/* Export instructions */}
      <section className="bg-film-black text-white p-8 mb-12" data-id="element-1504">
        <p className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-3" data-id="element-1505">
          Export
        </p>
        <h2 className="font-display font-bold text-2xl mb-4" data-id="element-1506">
          Instagram sizes
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs" data-id="element-1507">
          <div data-id="element-1508">
            <p className="text-white/50 mb-1" data-id="element-1509">REEL / STORY</p>
            <p className="text-white" data-id="element-1510">1080×1920</p>
          </div>
          <div data-id="element-1511">
            <p className="text-white/50 mb-1" data-id="element-1512">CAROUSEL</p>
            <p className="text-white" data-id="element-1513">1080×1350</p>
          </div>
          <div data-id="element-1514">
            <p className="text-white/50 mb-1" data-id="element-1515">SQUARE</p>
            <p className="text-white" data-id="element-1516">1080×1080</p>
          </div>
          <div data-id="element-1517">
            <p className="text-white/50 mb-1" data-id="element-1518">EXPORT FORMAT</p>
            <p className="text-white" data-id="element-1519">PNG / JPG @ 1x</p>
          </div>
        </div>
      </section>
    </div>;
}